import {
  createToolRegistry,
  createToolComponent,
  OperatorResultTool,
  RagResultTool,
  ChatRenderer,
  type ToolComponentProps,
  WorkingHoursContext,
  type MessagesGroup,
  UsageLimitsErrorTool,
  MembershipTool,
  GroupClassesTool,
  AuthenticationTool,
} from '@form-factory/chatbot-ui';
import type { ChatMessageType, ThreadType } from '@form-factory/schema';
import { ToolNames } from '@form-factory/schema/contracts';
import { ChatRole, ThreadMode } from '@form-factory/schema/enums';
import { motion } from 'motion/react';
import { memo, useCallback, useMemo } from 'react';

import avatar from '@/assets/images/avatar.jpg';
import { useChatConfig } from '@/contexts/ChatConfigProvider';

import type { UseChatReturn } from './chatTypes';
import { FAQs } from '../../components/faqs/FAQs';
import { Feedback } from '../../components/feedback/Feedback';

export interface ChatbotChatRendererProps {
  thread: ThreadType | null;
  messages: ChatMessageType[];
  insert: UseChatReturn['insert'];
  isInFAQLoop: boolean;
  faqIndex: number | null;
  isLoading: boolean;
  isFetching: boolean;
}

export const ChatbotChatRenderer = memo(
  ({
    thread,
    messages,
    insert,
    isInFAQLoop,
    faqIndex,
    isLoading,
    isFetching,
  }: ChatbotChatRendererProps) => {
    const { agentName, chatbotConfig, workingHours } = useChatConfig();
    const agentAvatar = chatbotConfig?.systemAvatar || avatar;

    const shouldRenderFeedback = useCallback(
      (group: MessagesGroup) => {
        // Do not render feedback when turned off in the config
        if (!chatbotConfig?.showFeedbackButtons) {
          return false;
        }

        // Do not render feedback for operator tool calls
        if (group.role === ChatRole.Assistant) {
          return !group.messages.some(message =>
            message.toolCalls?.some(
              toolCall =>
                toolCall.type === 'tool' &&
                toolCall.name === ToolNames.RagResultOperator,
            ),
          );
        }

        return true;
      },
      [chatbotConfig?.showFeedbackButtons],
    );

    const toolRegistry = useMemo(
      () =>
        createToolRegistry({
          [ToolNames.AuthRequired]: AuthenticationTool,
          [ToolNames.Membership]: MembershipTool,
          [ToolNames.UsageLimitsError]: UsageLimitsErrorTool,
          [ToolNames.GroupClass]: GroupClassesTool,
          [ToolNames.RagResult]: createToolComponent(
            {},
            (props: ToolComponentProps) => {
              // Get component in PascalCase to satisfy linter
              const RagResultComponent = RagResultTool.component;

              return (
                <RagResultComponent
                  {...props}
                  sourcesFormat={chatbotConfig?.sourcesFormat}
                />
              );
            },
          ),
          [ToolNames.RagResultOperator]: createToolComponent(
            {},
            (props: ToolComponentProps) => {
              // Get component in PascalCase to satisfy linter
              const OperatorResultComponent = OperatorResultTool.component;

              return (
                <OperatorResultComponent
                  {...props}
                  mode={thread?.mode ?? ThreadMode.AI}
                />
              );
            },
          ),
        }),
      [chatbotConfig?.sourcesFormat, thread],
    );

    return (
      <WorkingHoursContext.Provider value={workingHours}>
        <ChatRenderer
          messages={messages}
          isFetching={isFetching}
          toolRegistry={toolRegistry}
          shouldRenderFeedback={shouldRenderFeedback}
          agentName={agentName}
          agentAvatar={agentAvatar}
          FeedbackComponent={Feedback}
          renderAfterGroup={(_, index) => {
            if (isInFAQLoop && index === faqIndex && !isLoading) {
              return (
                <motion.div
                  key='faqs'
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <FAQs insert={insert} />
                </motion.div>
              );
            }

            return null;
          }}
        />
      </WorkingHoursContext.Provider>
    );
  },
);
